import { Analysis, AnalysisCalculation } from '@marmon/shared/types/analysis';
import { DocumentReference, Timestamp } from '@marmon/shared/types/common';
import { ReportStatuses, ReportTypes } from '../constants';
import { Report, ReportEightyTwenty, ReportQBR } from '../interfaces';

type CreateReportPayloadEightyTwenty = Pick<
	ReportEightyTwenty,
	'name' | 'domain' | 'type'
>;
type CreateReportPayloadQBR = Pick<
	ReportQBR,
	'name' | 'domain' | 'type' | 'metadata'
>;

export function createReport(
	payload: CreateReportPayloadEightyTwenty | CreateReportPayloadQBR,
	options: {
		analysis: DocumentReference<Analysis>;
		calculation: DocumentReference<AnalysisCalculation>;
	},
): Partial<Report> {
	const { name, domain, type } = payload;
	const { analysis, calculation } = options;

	const base = {
		name,
		domain,
		publishAt: null,
		type,
		analysis,
		calculation,
		status: ReportStatuses.Draft,
		version: 1,
		latestStatusEventRef: null,
	};

	switch (type) {
		case ReportTypes.EightyTwenty:
			return base as Partial<ReportEightyTwenty>;
		case ReportTypes.QBR:
			return {
				...base,
				metadata: payload.metadata,
			} as Partial<ReportQBR>;
		default:
			throw new Error(`Invalid report type: ${type}`);
	}
}

/**
 * Stamp `publishAt` whenever the report is placed in review.
 * Resubmits overwrite with the later submit time. Other transitions keep
 * the existing value and never clear it.
 */
export function nextReportPublishAt(
	report: Pick<Report, 'publishAt' | 'status'>,
	newStatus: string,
	now: Timestamp,
): Timestamp | null {
	if (newStatus.toLowerCase() === ReportStatuses.Submitted) {
		return now;
	}

	return report.publishAt ?? null;
}

/**
 * Status-event and activity types that mean the report was placed in review.
 * Status events use `SUBMITTED`; the generic activity log uses `submit`.
 */
export function isReportSubmitHistoryType(type: unknown): boolean {
	if (typeof type !== 'string') return false;
	const normalized = type.trim().toLowerCase().replace(/[_-]/g, '');
	return normalized === 'submitted' || normalized === 'submit';
}

export const generateLink = (
	location: string,
	reportId: string,
	analysisId: string,
) => `${location}/analysis/${analysisId}/reports/${reportId}/view`;

/**
 * i18n key for displaying report status in the UI (capitalized).
 * Usage: transloco.translate(getReportStatusI18nKey(status))
 */
export const getReportStatusI18nKey = (status: string): string =>
	`report.status.${status}`;

/**
 * i18n key for displaying report type in the UI.
 * Usage: transloco.translate(getReportTypeI18nKey(type))
 */
export const getReportTypeI18nKey = (type: string): string => {
	const keyMap: Record<string, string> = {
		'eighty-twenty': 'report.dialog.create-report.type.eightyTwenty',
		qbr: 'report.dialog.create-report.type.qbr',
	};
	return keyMap[type] ?? 'report.dialog.create-report.type.eightyTwenty';
};

/**
 * Checks if a report is editable based on its status.
 * Reports can be edited when:
 * - Status is DRAFT (initial state)
 * - Status is CHANGES_REQUESTED (can resubmit after changes)
 *
 * Reports cannot be edited when:
 * - Status is SUBMITTED (locked for review)
 * - Status is APPROVED (final state, no further edits)
 *
 * @param status The report status to check
 * @returns true if the report can be edited, false otherwise
 */
export function isReportEditable(status: string | undefined | null): boolean {
	if (!status) return true; // Default to editable if status is missing (backwards compatibility)
	const normalizedStatus = status.toLowerCase();
	return (
		normalizedStatus === ReportStatuses.Draft ||
		normalizedStatus === ReportStatuses.ChangesRequested
	);
}

/**
 * Checks if a single metadata property value is non-empty.
 * - null/undefined: empty
 * - string: empty if blank after trim
 * - string[]: empty if array is empty or any element is blank after trim
 * - other primitives: non-empty
 */
function isMetadataValueNonEmpty(value: unknown): boolean {
	if (value == null) return false;
	if (typeof value === 'string') return value.trim() !== '';
	if (Array.isArray(value)) {
		// Treat any non-empty array as having a value, even if some entries
		// are empty strings (e.g. QBR segments can include an empty segment).
		return value.length > 0;
	}
	return true;
}

/**
 * Validates that report metadata is complete when required.
 * When typeRequiresMetadata is true, the metadata object must exist and all
 * property values must be non-empty (no null, undefined, or empty strings).
 * For string arrays (e.g. segments), the array must have at least one non-empty string.
 * Works generically for any report type metadata structure.
 */
export function isReportMetadataValid(
	metadata: unknown,
	typeRequiresMetadata: boolean,
): boolean {
	if (!typeRequiresMetadata) return true;
	if (
		metadata == null ||
		typeof metadata !== 'object' ||
		Array.isArray(metadata)
	) {
		return false;
	}
	const obj = metadata as Record<string, unknown>;
	const values = Object.values(obj);
	if (values.length === 0) return false;
	return values.every(isMetadataValueNonEmpty);
}
